// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene
export const SCENE_COLORS = {
  scene1: { primaryColor: '#6B7280', accentColor: '#EF4444' },   // Gray/Red - Muted, concerning
  scene2: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Energetic
  scene3: { primaryColor: '#ffffff', accentColor: '#F59E0B' },   // White/Amber - Professional
  scene4: { primaryColor: '#F59E0B', accentColor: '#ffffff' },   // Amber/White - Urgency
  scene5: { primaryColor: '#EF4444', accentColor: '#ffffff' },   // Red/White - Dramatic
  scene6: { primaryColor: '#3B82F6', accentColor: '#FCD34D' },   // Blue/Gold - Professional
  scene7: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - Premium
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },     // White/Gold - Brand
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/pvm-quiet-phone/scene1-quiet-phone.png',
  scene2: 'images/pvm-quiet-phone/scene2-ringing-phone.png',
  scene3: 'images/pvm-quiet-phone/scene3-county-map.png',
  scene4: 'images/pvm-quiet-phone/scene4-paving-job.png',
  scene5: 'images/pvm-quiet-phone/scene5-search-results.png',
  scene6: 'images/pvm-quiet-phone/scene6-analytics.png',
  scene7: 'images/pvm-quiet-phone/scene7-report.png',
};

// Hg market report presentation slides (captured from reports.pavingmarketers.com)
// Source: https://reports.pavingmarketers.com/p/Bdui4YDzhvQI
export const REPORT_SLIDES = [
  'images/pvm-quiet-phone/report-slide-01.png',
  'images/pvm-quiet-phone/report-slide-05.png',
  'images/pvm-quiet-phone/report-slide-07.png',
  'images/pvm-quiet-phone/report-slide-12.png',
];

// Word timings extracted from audio using Whisper (incredibly-fast-whisper)
// Voice: Mark (UgBBYS2sOqTuMpoF3BR0) - American male
// Manual corrections: punctuation + capitalization
export const WORD_TIMINGS: WordTiming[] = [
  { text: 'Your', startMs: 0, endMs: 120 },
  { text: 'phone.', startMs: 120, endMs: 540 },
  { text: 'Quiet.', startMs: 540, endMs: 2060 },
  { text: 'Your', startMs: 2060, endMs: 3200 },
  { text: 'competitor\'s', startMs: 3200, endMs: 3740 },
  { text: 'phone.', startMs: 3740, endMs: 4140 },
  { text: 'Ringing.', startMs: 4140, endMs: 5080 },
  { text: 'Same', startMs: 5080, endMs: 6280 },
  { text: 'county.', startMs: 6280, endMs: 6840 },
  { text: 'Same', startMs: 6840, endMs: 7500 },
  { text: 'services.', startMs: 7500, endMs: 8240 },
  { text: 'Different', startMs: 8240, endMs: 8980 },
  { text: 'results.', startMs: 8980, endMs: 10060 },
  { text: 'They\'re', startMs: 10060, endMs: 10700 },
  { text: 'landing', startMs: 10700, endMs: 10980 },
  { text: 'the', startMs: 10980, endMs: 11100 },
  { text: 'parking', startMs: 11100, endMs: 11380 },
  { text: 'lot', startMs: 11380, endMs: 11600 },
  { text: 'jobs.', startMs: 11600, endMs: 12200 },
  { text: 'The', startMs: 12200, endMs: 12760 },
  { text: 'asphalt', startMs: 12760, endMs: 13140 },
  { text: 'contracts.', startMs: 13140, endMs: 13980 },
  { text: 'The', startMs: 13980, endMs: 14580 },
  { text: 'spring', startMs: 14580, endMs: 14860 },
  { text: 'reseal', startMs: 14860, endMs: 15340 },
  { text: 'work.', startMs: 15340, endMs: 15900 },
  { text: 'Why?', startMs: 15900, endMs: 17260 },
  { text: 'They', startMs: 18260, endMs: 18360 },
  { text: 'own', startMs: 18360, endMs: 18580 },
  { text: 'the', startMs: 18580, endMs: 18680 },
  { text: 'search', startMs: 18680, endMs: 18900 },
  { text: 'terms', startMs: 18900, endMs: 19180 },
  { text: 'you', startMs: 19180, endMs: 19360 },
  { text: 'don\'t.', startMs: 19360, endMs: 19680 },
  { text: 'We', startMs: 20180, endMs: 20280 },
  { text: 'map', startMs: 20280, endMs: 20440 },
  { text: 'your', startMs: 20440, endMs: 20600 },
  { text: 'entire', startMs: 20600, endMs: 20820 },
  { text: 'market.', startMs: 20820, endMs: 21360 },
  { text: 'Who', startMs: 21860, endMs: 21960 },
  { text: 'ranks', startMs: 21960, endMs: 22160 },
  { text: 'above', startMs: 22160, endMs: 22380 },
  { text: 'you.', startMs: 22380, endMs: 22760 },
  { text: 'What\'s', startMs: 23260, endMs: 23420 },
  { text: 'working.', startMs: 23420, endMs: 23840 },
  { text: 'What\'s', startMs: 24340, endMs: 24540 },
  { text: 'not.', startMs: 24540, endMs: 24920 },
  { text: 'Free', startMs: 25420, endMs: 25560 },
  { text: 'Market', startMs: 25560, endMs: 25740 },
  { text: 'Report.', startMs: 25740, endMs: 26240 },
  { text: 'Grab', startMs: 26240, endMs: 26520 },
  { text: 'yours', startMs: 26520, endMs: 26720 },
  { text: 'now.', startMs: 26720, endMs: 27120 },
];

// Timing helpers for stitched V3 voiceover
export const PART1_END_MS = WORD_TIMINGS[26].endMs;
export const PART2A_START_MS = WORD_TIMINGS[27].startMs;
export const PART2A_END_MS = WORD_TIMINGS[33].endMs;
export const PART2B_START_MS = WORD_TIMINGS[34].startMs;
export const PART2B_END_MS = WORD_TIMINGS[38].endMs;
export const PART2C_START_MS = WORD_TIMINGS[39].startMs;
export const PART2C_END_MS = WORD_TIMINGS[42].endMs;
export const PART2D_START_MS = WORD_TIMINGS[43].startMs;
export const PART2D_END_MS = WORD_TIMINGS[44].endMs;
export const PART2E_START_MS = WORD_TIMINGS[45].startMs;
export const PART2E_END_MS = WORD_TIMINGS[46].endMs;
export const PART2F_START_MS = WORD_TIMINGS[47].startMs;
export const PART2F_END_MS = WORD_TIMINGS[52].endMs;
export const VOICEOVER_END_MS = WORD_TIMINGS[WORD_TIMINGS.length - 1].endMs;
export const LOGO_PAUSE_MS = 1000;
export const LOGO_DURATION_MS = 5040;

// Scene definitions with word groupings (aligned to Whisper timing)
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 2060, // Before "Your competitor's"
    words: WORD_TIMINGS.slice(0, 3), // "Your phone. Quiet."
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Contrast',
    startMs: 2060,
    endMs: 5080, // Before "Same county"
    words: WORD_TIMINGS.slice(3, 7), // "Your competitor's phone. Ringing."
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Setup',
    startMs: 5080,
    endMs: 10060, // Before "They're landing"
    words: WORD_TIMINGS.slice(7, 13), // "Same county. Same services. Different results."
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 4,
    name: 'Pain',
    startMs: 10060,
    endMs: 15900, // Before "Why?"
    words: WORD_TIMINGS.slice(13, 26), // "They're landing... spring reseal work."
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomInLower,
  },
  {
    id: 5,
    name: 'Question',
    startMs: 15900,
    endMs: PART2B_START_MS, // Before "We map"
    words: WORD_TIMINGS.slice(26, 34), // "Why? They own the search terms you don't."
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 6,
    name: 'Solution',
    startMs: PART2B_START_MS,
    endMs: PART2F_START_MS, // Before "Free Market Report"
    words: WORD_TIMINGS.slice(34, 47), // "We map your entire market... What's not."
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 7,
    name: 'CTA',
    startMs: PART2F_START_MS,
    endMs: VOICEOVER_END_MS + LOGO_PAUSE_MS, // End of voiceover + logo pause
    words: WORD_TIMINGS.slice(47), // "Free Market Report. Grab yours now."
    ...SCENE_COLORS.scene7,
    backgroundImage: SCENE_IMAGES.scene7,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene (after voiceover) - full animated logo playback
export const LOGO_SCENE = {
  startMs: VOICEOVER_END_MS + LOGO_PAUSE_MS,
  endMs: VOICEOVER_END_MS + LOGO_PAUSE_MS + LOGO_DURATION_MS,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
